import { PLAYER_SEARCH_MAX_LENGTH, describeApiError, type RoomPlayer } from '@brush/api-client';
import { ErrorRetry, Spinner, useDebouncedValue } from '@brush/ui';
import { FileUp, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useContactCoverage,
  useCreatePlayer,
  usePatchPlayer,
  useRoomPlayerPage,
  useUnclaimedPlaces,
} from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { unclaimedCount } from '../../unclaimed/places';
import { Pager } from '../tournaments/Pager';
import { MembershipsPanel } from './MembershipsPanel';
import { UnclaimedPlacesView } from './UnclaimedPlacesView';
import { PlayerImportModal } from './PlayerImportModal';
import { PlayerModal } from './PlayerModal';
import {
  PLAYERS_PAGE_SIZE,
  PLAYERS_SEARCH_DEBOUNCE_MS,
  emptyMessage,
  pageForTerm,
  recordsStatus,
  settledPage,
  type PageChoice,
} from './recordsPage';

/**
 * What a room can be reached on, in the column an operator scans down.
 *
 * An em dash rather than a blank cell, because a blank reads as a rendering fault where a dash
 * reads as "nothing recorded" — the same distinction the tournament detail card makes for an
 * unrecorded buy-in count.
 */
/**
 * `absent` is a word rather than a dash wherever there is no column heading left to give the
 * dash its meaning — and on this screen an unreachable player is the actionable row, not an
 * empty cell: it is what the coverage line below the list is counting.
 */
function Contact({ player, absent = '—' }: { player: RoomPlayer; absent?: string }) {
  if (player.email === null && player.phone === null) {
    return <span className="text-ink-800/30">{absent}</span>;
  }

  return (
    <div className="min-w-0">
      {player.email ? <div className="truncate text-ink-950">{player.email}</div> : null}
      {player.phone ? <div className="truncate text-ink-800/60">{player.phone}</div> : null}
    </div>
  );
}

/**
 * How much of this room's list can be reached, stated plainly.
 *
 * Without it a room reads a claim flow that matches nobody as a broken feature rather than an
 * unpopulated one — and the number is also the work list, since the way to move it is to fill in
 * the players it is counting.
 */
function Coverage({ roomId }: { roomId: string }) {
  const coverage = useContactCoverage(roomId);
  if (!coverage.data) return null;

  const { total, reachable, withPhone } = coverage.data;
  if (total === 0) return null;

  return (
    <p className="mt-3 text-xs text-ink-800/60">
      <span className="font-medium text-ink-950">
        {reachable.toLocaleString()} of {total.toLocaleString()}
      </span>{' '}
      {/* Both words follow `total`, not `reachable` — "1 of 10 players have", "1 of 1 player
          has". Agreeing with `reachable` is what produced "1 of 10 player has", which reads as a
          typo on the one line of this screen whose job is to be believed; and it is `total` that
          governs, because the noun is the set being counted out of. §I of the manual tests has
          spelled this sentence out since it was written. */}
      {total === 1 ? 'player has' : 'players have'} an email address, so the rest cannot connect
      an account to their card yet.
      {withPhone > 0
        ? ` ${withPhone.toLocaleString()} ${withPhone === 1 ? 'has' : 'have'} a phone number recorded, which isn’t used for this yet.`
        : ''}
    </p>
  );
}

/** `onOpenTemplates` goes to Settings → Templates through the shell's guarded navigation (PI78's link). */
export function PlayersView({ onOpenTemplates }: { onOpenTemplates: () => void }) {
  const { room, roomId } = useRoom();
  const [search, setSearch] = useState('');
  // The server is asked once typing pauses; a pager click asks at once (the Players screen's Phase 0).
  const term = useDebouncedValue(search, PLAYERS_SEARCH_DEBOUNCE_MS).trim();
  const [choice, setChoice] = useState<PageChoice>({ term: '', page: 1 });
  // A new term starts at the first page; an edit keeps the page, which refetches in place.
  const page = pageForTerm(choice, term);
  const playersQ = useRoomPlayerPage(roomId, term, page, PLAYERS_PAGE_SIZE);
  const scrollToTop = useMainScroll();
  const createPlayer = useCreatePlayer(roomId);
  const patchPlayer = usePatchPlayer(roomId);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<RoomPlayer | null>(null);
  // The room's unclaimed places are a second view of this section: a claim is always "which player
  // finished here" (ADR 0013). The count on the switch is what brings an operator to it.
  const [showUnclaimed, setShowUnclaimed] = useState(false);
  const unclaimedQ = useUnclaimedPlaces(roomId);

  // A player renamed out of the term can leave the page past the end: the answer then carries no rows under a total
  // saying there are players. Step back to the last page, or the first when nobody is left.
  const answer = playersQ.data;
  const settled =
    answer && !playersQ.isPlaceholderData ? settledPage(page, answer.total, PLAYERS_PAGE_SIZE) : page;
  useEffect(() => {
    if (settled !== page) setChoice({ term, page: settled });
  }, [settled, page, term]);

  const status = recordsStatus({
    hasData: answer !== undefined,
    placeholder: playersQ.isPlaceholderData || settled !== page,
    failed: playersQ.isError,
  });
  if (status === 'loading') return <Spinner label="Loading players…" />;
  if (status === 'failed' || !room || !answer) {
    return (
      <div className="p-8">
        <ErrorRetry message="Couldn't load players." onRetry={() => void playersQ.refetch()} />
      </div>
    );
  }

  const players = answer.data;
  // Dimmed while the page on screen is not the one asked for, or the box is ahead of the search.
  const updating = status === 'updating' || search.trim() !== term;

  if (showUnclaimed && roomId) {
    return (
      <UnclaimedPlacesView
        roomId={roomId}
        onBack={() => setShowUnclaimed(false)}
      />
    );
  }
  const unclaimed = unclaimedQ.data?.length ?? 0;
  /* One sentence, read by both arrangements. "None registered" and "none matched" send an
     operator to different places, so `emptyMessage` decides it from the total and the term. The
     server matches the card, the whole name and the email — the email because this is the screen
     where a contact is corrected, and an operator reading a bounced email has an address to go on. */
  const empty = emptyMessage(answer.total, term);
  const goToPage = (next: number) => {
    setChoice({ term, page: next });
    scrollToTop();
  };

  return (
    <div className="p-8">
      {/* The section header arrangement the tournaments list sets: `flex-wrap` with `gap-3` so a
          long heading or a wider face drops the button to its own row rather than squashing the
          title, `min-w-0` so the description wraps instead of widening the row past the viewport,
          and `shrink-0` so the buttons are not compressed before the wrap. No overflow menu — two
          actions, Import players and Add player, which stay at every size and wrap together. */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold text-ink-950">Players</h1>
          <p className="mt-1 text-sm text-ink-800/60">Players registered at {room.name}.</p>
          <button
            type="button"
            onClick={() => setShowUnclaimed(true)}
            className="mt-1 text-sm font-medium text-felt-700 hover:underline"
          >
            {unclaimed > 0 ? `${unclaimedCount(unclaimed)} to claim` : 'Unclaimed places'}
          </button>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setImporting(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm font-medium text-ink-800 hover:bg-ink-800/5"
          >
            <FileUp className="size-4" /> Import players
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-felt-700 px-4 py-2 text-sm font-medium text-white hover:bg-felt-900"
          >
            <Plus className="size-4" /> Add player
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-800/40" />
          {/* A search field (#154), so it draws its own × at the right of its content box — which is why
              the right padding is the ordinary one: anything wider puts the × that far in from the edge. */}
          <input
            type="search"
            autoComplete="off"
            spellCheck={false}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={PLAYER_SEARCH_MAX_LENGTH}
            placeholder="Search by name, card ID or email…"
            className="w-full rounded-lg border border-ink-800/15 py-2 pl-10 pr-3 text-sm outline-none focus:border-felt-500"
          />
        </div>
        {/* The small indicator beside a list kept on screen: the previous page stays, dimmed, rather
            than blanking while the next one is on its way. Beside the box rather than over it, so it never
            shares the right edge with the ×; its slot is always reserved, so the box does not change width
            as it comes and goes. */}
        <span aria-live="polite" className="w-16 shrink-0 text-xs text-ink-800/50">
          {updating ? 'Updating…' : ''}
        </span>
      </div>

      <div
        className={`overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm transition-opacity ${
          updating ? 'opacity-60' : ''
        }`}
        aria-busy={updating}
      >
        {/*
         * Two arrangements, the same rule the three sheets follow. Six columns is the widest
         * table in the app, so this one had a scroller rather than clipping — but a sideways
         * scroll nested inside a vertical one is a poor gesture on touch, and it never lets an
         * operator see a name beside the winnings it belongs to.
         *
         * Below `md` the row folds to three lines. Name and total winnings hold the first, the
         * card and the two counts the second, and the contact the third — which stays rather
         * than demoting further because this is the screen where a contact is corrected, and an
         * unreachable player is the row an operator is here to act on.
         */}
        <ul className="divide-y divide-ink-800/5 md:hidden">
          {players.map((p) => (
            <li
              key={p.player.id}
              onClick={() => setEditing(p)}
              className="cursor-pointer px-4 py-3 hover:bg-felt-50/30"
            >
              <div className="flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  {/* The same split the row uses: the button carries the semantics with no
                      handler of its own, so its keyboard activation reaches the row by bubbling
                      and there is one control rather than two that can disagree. */}
                  <button
                    type="button"
                    className="block max-w-full truncate font-medium text-ink-950 hover:underline"
                  >
                    {p.player.name}
                  </button>
                  <span className="num mt-0.5 block text-xs text-ink-800/60">
                    {p.player.cardId} · {p.player.wins} win{p.player.wins === 1 ? '' : 's'} ·{' '}
                    {p.player.cashes} cashes
                  </span>
                </span>
                <span className="num shrink-0 text-sm font-medium text-ink-950">
                  ${p.player.totalWinnings.toLocaleString()}
                </span>
              </div>
              <div className="mt-1 text-xs">
                <Contact player={p} absent="No contact recorded" />
              </div>
            </li>
          ))}
          {empty ? (
            <li className="px-6 py-10 text-center text-sm text-ink-800/60">{empty}</li>
          ) : null}
        </ul>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
              <tr>
                <th className="px-6 py-3 font-medium">Player</th>
                <th className="px-6 py-3 font-medium">Card ID</th>
                <th className="px-6 py-3 font-medium">Contact</th>
                <th className="px-6 py-3 text-right font-medium">Wins</th>
                <th className="px-6 py-3 text-right font-medium">Cashes</th>
                <th className="px-6 py-3 text-right font-medium">Total winnings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-800/5">
              {players.map((p) => (
                <tr
                  key={p.player.id}
                  onClick={() => setEditing(p)}
                  className="cursor-pointer hover:bg-felt-50/30"
                >
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex size-8 items-center justify-center rounded-full bg-ink-950 text-xs font-semibold text-white">
                        {p.player.avatar}
                      </span>
                      {/* The button carries the semantics and the row carries the pointer: it has
                          no handler of its own, so its keyboard activation reaches the row by
                          bubbling and there is one control rather than two that can disagree. */}
                      <button type="button" className="font-medium text-ink-950 hover:underline">
                        {p.player.name}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-ink-800/70">{p.player.cardId}</td>
                  <td className="max-w-[16rem] px-6 py-3 text-xs">
                    <Contact player={p} />
                  </td>
                  <td className="px-6 py-3 text-right text-ink-950">{p.player.wins}</td>
                  <td className="px-6 py-3 text-right text-ink-950">{p.player.cashes}</td>
                  <td className="px-6 py-3 text-right font-medium text-ink-950">
                    ${p.player.totalWinnings.toLocaleString()}
                  </td>
                </tr>
              ))}
              {empty ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-ink-800/60">
                    {empty}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        {/* One pager for both arrangements: the page is the server's, so the phone list and the
            desktop table are the same 50 players and move together. */}
        <Pager
          page={answer.page}
          pageSize={answer.pageSize}
          total={answer.total}
          onPage={goToPage}
          label="Player pages"
        />
      </div>

      {roomId ? <Coverage roomId={roomId} /> : null}

      <p className="mt-2 text-xs text-ink-800/50">
        Wins, cashes, and winnings are totals the platform maintains from recorded tournament results.
      </p>

      {/* Below the list rather than beside it: the table is the room's record of its customers and
          this is a record of who has connected a *sign-in* to one of them — a smaller set, read
          for a different reason, and never the thing this screen is opened for. It sits under the
          coverage line on purpose, since that line is what says how many of these there could
          ever be. */}
      {roomId ? <MembershipsPanel roomId={roomId} /> : null}

      {adding ? (
        <PlayerModal
          roomId={room.id}
          saving={createPlayer.isPending}
          errorMessage={describeApiError(createPlayer.error)}
          onCreate={(body) => createPlayer.mutate(body, { onSuccess: () => setAdding(false) })}
          onPatch={() => undefined}
          onClose={() => setAdding(false)}
        />
      ) : null}

      {editing ? (
        <PlayerModal
          roomId={room.id}
          player={editing}
          saving={patchPlayer.isPending}
          errorMessage={describeApiError(patchPlayer.error)}
          onCreate={() => undefined}
          onPatch={(id, body) =>
            patchPlayer.mutate({ id, body }, { onSuccess: () => setEditing(null) })
          }
          onClose={() => setEditing(null)}
        />
      ) : null}

      {importing ? (
        <PlayerImportModal
          roomId={room.id}
          roomName={room.name}
          onClose={() => setImporting(false)}
          onOpenTemplates={onOpenTemplates}
        />
      ) : null}
    </div>
  );
}
